import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { LIBRARIES } from './map/mapUtils';
import { Input } from '../components/ui/input';
import { MapPin, MagnifyingGlass, SpinnerGap, Clock } from '@phosphor-icons/react';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

export default function AddressSearch({
  value, onChange, onSelect,
  placeholder = 'Search address...',
  testId,
  // Optional list of recent addresses to surface when the input is focused
  // and empty (Google Maps-style "Recently viewed" suggestion list). Items
  // must look like { place_id, address, lat, lng }. Clicking one bypasses
  // Google's geocode lookup and fires onSelect directly with the stored
  // coords — exactly what the caller saved last time.
  recentItems,
  onClearRecent,
}) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_KEY, libraries: LIBRARIES });

  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (value !== undefined && value !== query) {
      setQuery(value);
    }
  }, [value]);

  // Initialize Google services when API is loaded
  useEffect(() => {
    if (!isLoaded) return;
    if (window.google?.maps?.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      const div = document.createElement('div');
      placesService.current = new window.google.maps.places.PlacesService(div);
    }
  }, [isLoaded]);

  const searchAddress = useCallback(async (q) => {
    if (!q || q.length < 2 || !autocompleteService.current) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      autocompleteService.current.getPlacePredictions(
        { input: q, types: [] },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setResults(predictions);
            setShowDropdown(true);
          } else {
            setResults([]);
          }
          setLoading(false);
        }
      );
    } catch (e) {
      console.error('Places error:', e);
      setResults([]);
      setLoading(false);
    }
  }, []);

  function handleInputChange(e) {
    const val = e.target.value;
    setQuery(val);
    if (onChange) onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchAddress(val), 300);
  }

  function handleSelect(prediction) {
    setShowDropdown(false);
    setShowRecents(false);
    setResults([]);
    setQuery(prediction.description);
    if (onChange) onChange(prediction.description);

    // Emit the final onSelect once we have lat/lng. Wrapped so we can call
    // it from any of the three resolution paths below without duplication.
    const finalize = (loc, extras = {}) => {
      if (!loc) return;
      if (onSelect) {
        onSelect({
          address: prediction.description,
          lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
          lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
          type: prediction.types?.[0] || 'place',
          place_id: prediction.place_id,
          ...extras,
        });
      }
    };

    // (1) Primary: Places SDK getDetails — richest payload (name + formatted address)
    if (placesService.current && prediction.place_id) {
      placesService.current.getDetails(
        { placeId: prediction.place_id, fields: ['geometry', 'formatted_address', 'name'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
            finalize(place.geometry.location, {
              details: { name: place.name, formatted_address: place.formatted_address },
            });
            return;
          }
          // (2) Fallback: Geocoder by place_id — works even when the key doesn't
          //     have Places Details enabled or it's quota-limited. Same accuracy
          //     for the lat/lng, no formatted_address details.
          if (window.google?.maps?.Geocoder) {
            new window.google.maps.Geocoder().geocode(
              { placeId: prediction.place_id },
              (results, gStatus) => {
                if (gStatus === 'OK' && results?.[0]?.geometry?.location) {
                  finalize(results[0].geometry.location);
                } else if (window.google?.maps?.Geocoder) {
                  // (3) Last resort: geocode the raw description text.
                  new window.google.maps.Geocoder().geocode(
                    { address: prediction.description },
                    (rs2, st2) => {
                      if (st2 === 'OK' && rs2?.[0]?.geometry?.location) {
                        finalize(rs2[0].geometry.location);
                      }
                    }
                  );
                }
              }
            );
          }
        }
      );
    } else if (window.google?.maps?.Geocoder) {
      // No Places SDK ready — go straight to the geocoder.
      new window.google.maps.Geocoder().geocode(
        { address: prediction.description },
        (rs, st) => {
          if (st === 'OK' && rs?.[0]?.geometry?.location) finalize(rs[0].geometry.location);
        }
      );
    }
  }

  // Show recents whenever the input is focused with no query and we have
  // some history to display. Tracked separately from `showDropdown` because
  // the predictions and recents are mutually exclusive (predictions wins
  // once the user starts typing).
  const [showRecents, setShowRecents] = useState(false);
  const hasRecents = Array.isArray(recentItems) && recentItems.length > 0;

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId || 'address-search'}>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={(e) => {
            // Pre-select everything so the next keystroke replaces the
            // previous query — matches Google Maps' search box UX.
            try { e.target.select(); } catch (_) { /* noop: select() can throw on some inputs */ }
            if (results.length > 0) setShowDropdown(true);
            if (!query && hasRecents) setShowRecents(true);
          }}
          placeholder={placeholder}
          className="pl-9 pr-9 rounded-md h-[38px]"
          data-testid={testId ? `${testId}-input` : 'address-search-input'}
        />
        {loading && (
          <SpinnerGap size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
        )}
        {query && !loading && (
          <button
            type="button"
            onClick={() => {
              // Clear the typed query AND any selected result. We don't fire
              // onSelect(null) because callers don't generally accept null;
              // a separate clear signal would be invasive. Callers that need
              // to clear the loaded address (Quick Look) have their own
              // address-pill × button alongside this input.
              setQuery('');
              setResults([]);
              setShowDropdown(false);
              if (onChange) onChange('');
            }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Clear search"
            aria-label="Clear search"
            data-testid={testId ? `${testId}-clear` : 'address-search-clear'}
          >
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path d="M10 8.586l4.293-4.293 1.414 1.414L11.414 10l4.293 4.293-1.414 1.414L10 11.414l-4.293 4.293-1.414-1.414L8.586 10 4.293 5.707l1.414-1.414L10 8.586z"/></svg>
          </button>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div className="absolute z-[9999] mt-1 w-full bg-card border border-border rounded-md shadow-dropdown max-h-72 overflow-y-auto" data-testid="address-results-dropdown">
          {results.map((prediction, i) => {
            const main = prediction.structured_formatting?.main_text || prediction.description;
            const secondary = prediction.structured_formatting?.secondary_text || '';
            return (
              <button
                key={prediction.place_id || i}
                onClick={() => handleSelect(prediction)}
                className="w-full text-left px-3 py-2.5 hover:bg-secondary/80 transition-colors flex items-start gap-2.5 border-b border-border/30 last:border-0"
                data-testid={`address-result-${i}`}
              >
                <MapPin size={15} className="text-primary mt-0.5 shrink-0" weight="fill" />
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-snug truncate">{main}</p>
                  {secondary && <p className="text-xs text-muted-foreground truncate mt-0.5">{secondary}</p>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Recently viewed panel — shown only when the input is focused with
          empty query and we have history. Each row is a stored address that
          fires onSelect directly (no Google geocode round-trip needed). */}
      {showRecents && !query && hasRecents && results.length === 0 && (
        <div className="absolute z-[9999] mt-1 w-full bg-card border border-border rounded-md shadow-dropdown max-h-80 overflow-y-auto" data-testid="address-recents-dropdown">
          <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Recently viewed</div>
          {recentItems.map((item, i) => {
            const main = (item.address || '').split(',')[0] || item.address;
            const secondary = (item.address || '').split(',').slice(1).join(',').trim();
            return (
              <button
                key={item.place_id || i}
                onClick={() => {
                  setShowRecents(false);
                  setShowDropdown(false);
                  // Keep the search box empty after picking a recent item —
                  // the currently-loaded address is already shown via the
                  // pill next to the input, so duplicating it here adds
                  // noise the user has to clear before searching again.
                  setQuery('');
                  setResults([]);
                  onChange?.('');
                  onSelect?.({
                    address: item.address,
                    lat: item.lat,
                    lng: item.lng,
                    place_id: item.place_id,
                    type: 'recent',
                  });
                }}
                className="w-full text-left px-3 py-2.5 hover:bg-secondary/80 transition-colors flex items-start gap-2.5 border-b border-border/30 last:border-0"
                data-testid={`address-recent-${i}`}
              >
                <Clock size={15} className="text-muted-foreground mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-snug truncate">{main}</p>
                  {secondary && <p className="text-xs text-muted-foreground truncate mt-0.5">{secondary}</p>}
                </div>
              </button>
            );
          })}
          {onClearRecent && (
            <button
              type="button"
              onClick={() => { onClearRecent(); setShowRecents(false); }}
              className="w-full text-left px-3 py-2 text-xs font-bold uppercase tracking-wider text-primary hover:bg-secondary/60 transition-colors"
              data-testid="address-recents-clear"
            >
              Clear history
            </button>
          )}
        </div>
      )}
    </div>
  );
}
